import {
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  ModalBody,
  ModalFooter,
  ModalHeader,
  multiFilter,
  numericColumn,
  selectColumn,
  Switch,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { priceCell } from "src/components";
import { Stage } from "src/generated/graphqlTypes";
import {
  BulkAddProjectItem_ProjectItemFragment,
  useBulkAddProjectItemsQuery,
} from "src/routes/components/BulkAddProjectItemsModal.generated";
import { assertNever, costTypeToNameMapper, groupBy, hasData, renderLoadingOrError } from "src/utils";

type HiddenProjectItemsProp = string[] | ((projectItem: BulkAddProjectItem_ProjectItemFragment) => boolean);

export type BulkAddProjectItemsModalProps = {
  stage: Stage;
  projectId: string;
  onAdd: (projectItemId: string[], projectItems: BulkAddProjectItem_ProjectItemFragment[], isRemove: boolean) => void;
  groupBy: CeliGroupByValue;
  hiddenProjectItemIds: HiddenProjectItemsProp;
  submitButtonText: string;
  // When true, show a toggle to add the selected items as removal lines (CELI type=Remove)
  allowRemove?: boolean;
};

export enum CeliGroupByValue {
  CostCode = "costCode",
  CurrentTradePartner = "currentTradePartner",
}

/** Beam's widest preset, and tall enough that the item table doesn't open as a short strip. */
export const BulkAddProjectItemsModalSize = { width: "3xl" as const, height: 900 };

const visibleColumnsStorageKey = "bulkAddProjectItemsColumns";

export function BulkAddProjectItemsModal(props: BulkAddProjectItemsModalProps) {
  const { stage, projectId, groupBy, onAdd, hiddenProjectItemIds, submitButtonText, allowRemove = false } = props;
  const testIds = useTestIds({}, "bulkAddItemsModal");
  const query = useBulkAddProjectItemsQuery({ variables: { projectId, stage } });
  const projectItems = useMemo(
    () => query.data?.project.stage.projectItems || [],
    [query.data?.project.stage.projectItems],
  );
  const { closeModal } = useModal();
  const [isRemove, setIsRemove] = useState(false);

  const columns = useMemo(() => createColumns(groupBy), [groupBy]);
  const filterDefs = useMemo(() => createFilterDefs(projectItems), [projectItems]);
  // In a modal this stays in session storage, keyed per project so another project's locations don't come back.
  const layoutState = useGridTableLayoutState<Filter>({
    persistedFilter: { filterDefs, storageKey: `bulkAddProjectItemsFilter_${projectId}` },
    search: "client",
  });
  const rows = useMemo(
    () => createRows(projectItems, layoutState.filter, groupBy, hiddenProjectItemIds),
    [projectItems, groupBy, layoutState.filter, hiddenProjectItemIds],
  );
  const tableApi = useGridTableApi<Row>();

  const selectedProjectItems = useComputed(() => {
    return tableApi.getSelectedRowIds("lineItem");
  }, [tableApi]);

  if (!hasData(query)) {
    return renderLoadingOrError(query);
  }

  return (
    <>
      <ModalHeader>Select Items</ModalHeader>
      <ModalBody>
        <div {...testIds}>
          <GridTableLayout
            selectionSummary
            layoutState={layoutState}
            emptyFallback="No Line Items found for the selected projects."
            tableProps={{
              api: tableApi,
              columns,
              rows,
              sorting: { on: "client", initial: ["group", "DESC"] },
              visibleColumnsStorageKey,
            }}
          />
        </div>
      </ModalBody>
      <ModalFooter xss={Css.if(allowRemove).jcsb.$}>
        {allowRemove && (
          <Switch
            label="Remove"
            labelStyle="filter"
            tooltip="Add the selected items as removal lines"
            selected={isRemove}
            onChange={setIsRemove}
          />
        )}
        <div css={Css.df.gap1.$}>
          <Button variant="quaternary" label="Cancel" onClick={closeModal} />
          <Button
            variant="primary"
            // Make it obvious the selected items will be added as removal lines
            label={isRemove ? "Add as Removal" : submitButtonText}
            disabled={!selectedProjectItems?.length}
            onClick={() => {
              const pIds = selectedProjectItems || [];
              closeModal();
              onAdd(
                pIds,
                pIds.map((id) => projectItems.find((pi) => pi.id === id) as BulkAddProjectItem_ProjectItemFragment),
                isRemove,
              );
            }}
          />
        </div>
      </ModalFooter>
    </>
  );
}

type HeaderRow = Record<string, any>;
type Row =
  | { kind: "header"; data: HeaderRow; id: string }
  | { kind: "group"; data: BulkAddProjectItem_ProjectItemFragment[]; id: string }
  | { kind: "lineItem"; data: BulkAddProjectItem_ProjectItemFragment; id: string };
type Filter = { locationId?: string[] };

function createRows(
  projectItems: BulkAddProjectItem_ProjectItemFragment[],
  filter: Filter,
  groupBy: CeliGroupByValue,
  hiddenProjectItemIds: HiddenProjectItemsProp,
): GridDataRow<Row>[] {
  /** Group project items by costCode or TradeCategory keys */
  const groupedProjects = getProjectsItems(projectItems, groupBy, filter, hiddenProjectItemIds);

  return [
    { kind: "header", id: "header", data: groupedProjects },
    ...groupedProjects.map(([gr, pi]) => ({
      kind: "group" as const,
      id: gr,
      data: pi,
      children: pi.map((i: BulkAddProjectItem_ProjectItemFragment) => ({
        kind: "lineItem" as const,
        id: i.id,
        data: i,
      })),
    })),
  ];
}

function createColumns(groupBy: CeliGroupByValue): GridColumn<Row>[] {
  const isGroupedByCostCode = groupBy === CeliGroupByValue.CostCode;
  return [
    collapseColumn<Row>({}),
    selectColumn<Row>({ w: "32px" }),
    column<Row>({
      id: "group",
      header: isGroupedByCostCode ? "Cost Code" : "Trade Partner",
      name: isGroupedByCostCode ? "Cost Code" : "Trade Partner",
      canHide: false,
      group: (data) =>
        isGroupedByCostCode ? data[0]?.item?.costCode?.displayName : data[0]?.currentTradePartner?.name,
      lineItem: (data) => data?.displayName,
      w: 2,
      mw: "240px",
    }),
    column<Row>({
      id: "id",
      name: "Id",
      header: emptyCell,
      group: emptyCell,
      lineItem: (data) => data.id,
      mw: "100px",
    }),
    column<Row>({
      id: "poNumber",
      header: "PO#",
      name: "PO#",
      group: emptyCell,
      lineItem: (data) => data.currentCommitment?.accountingNumber,
      w: 1,
      mw: "100px",
    }),
    column<Row>({
      id: "location",
      header: "Location",
      name: "Location",
      group: emptyCell,
      lineItem: (data) => data.location.name,
      w: 1,
      mw: "140px",
    }),
    column<Row>({
      id: "costType",
      header: "Cost Type",
      name: "Cost Type",
      group: emptyCell,
      lineItem: (data) => data.costType && costTypeToNameMapper[data.costType],
      w: 1,
      mw: "120px",
    }),
    column<Row>({
      id: "bidItem",
      header: "Bid Item",
      name: "Bid Item",
      group: emptyCell,
      lineItem: (data) => data.bidItem?.name,
      mw: "140px",
    }),
    numericColumn<Row>({
      id: "cost",
      header: "Cost",
      name: "Cost",
      group: (data) => priceCell({ valueInCents: data.sum((pi) => pi.totalCostInCents) }),
      lineItem: (data) => priceCell({ valueInCents: data.totalCostInCents }),
      w: 1,
      mw: "100px",
    }),
    numericColumn<Row>({
      id: "tradeBilled",
      header: "Trade Billed",
      name: "Trade Billed",
      group: (data) => priceCell({ valueInCents: data.sum((pi) => pi.budgetFinancials.tradeBilled) }),
      lineItem: (data) => priceCell({ valueInCents: data.budgetFinancials.tradeBilled }),
      w: 1,
      mw: "120px",
    }),
  ];
}

function createFilterDefs(projectItems: BulkAddProjectItem_ProjectItemFragment[]): FilterDefs<Filter> {
  const allLocations = projectItems.map(({ location }) => location).filter((l) => !!l);
  const locations = [...new Map(allLocations?.map((location) => [location!.id, location])).values()];
  return {
    locationId: multiFilter({
      label: "Location",
      options: locations,
      getOptionValue: (location) => location!.id,
      getOptionLabel: (location) => location!.name,
    }),
  };
}

function getProjectsItems(
  projectItems: BulkAddProjectItem_ProjectItemFragment[],
  groupedBy: CeliGroupByValue,
  filter: Filter,
  hiddenProjectItemIds: HiddenProjectItemsProp,
): [string, BulkAddProjectItem_ProjectItemFragment[]][] {
  const isOmittedItem = (pi: BulkAddProjectItem_ProjectItemFragment) =>
    (filter?.locationId && (!pi?.location.id || !filter.locationId?.includes(pi?.location.id))) ||
    Array.isArray(hiddenProjectItemIds)
      ? (hiddenProjectItemIds as string[]).includes(pi.id)
      : hiddenProjectItemIds(pi);

  const groupedItems = groupBy(
    projectItems.filter((pi) => !isOmittedItem(pi) && getGroupByValue(groupedBy, pi)),
    (pi) => getGroupByValue(groupedBy, pi) || "",
  );
  return Object.entries(groupedItems);
}

function getGroupByValue(groupBy: CeliGroupByValue, pi: BulkAddProjectItem_ProjectItemFragment): string | undefined {
  switch (groupBy) {
    case CeliGroupByValue.CostCode:
      return pi.item[CeliGroupByValue.CostCode].id;
    case CeliGroupByValue.CurrentTradePartner:
      return pi[CeliGroupByValue.CurrentTradePartner]?.id;
    default:
      return assertNever(groupBy);
  }
}
